import { Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { CeRequestStatus } from '../../models/ce-request.model';
import { ViewRequestData } from '../../models/view-request.model';
import { ProjectsApiService, ProjectDto } from '../../services/projects-api.service';
import { CeRequestStateService } from '../../services/ce-request-state.service';

@Component({
  selector: 'app-view-request',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './view-request.html',
  styleUrl: './view-request.scss',
})
export class ViewRequestComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly state = inject(CeRequestStateService);

  protected readonly request = signal<ViewRequestData | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly projectId = signal<number | null>(null);

  constructor() {
    void this.loadProject();
  }

  private async loadProject(): Promise<void> {
    const projectIdParam = this.route.snapshot.queryParamMap.get('projectId');
    if (!projectIdParam) {
      this.request.set(this.mapFromDraftState());
      this.isLoading.set(false);
      return;
    }

    const projectId = Number(projectIdParam);
    if (!Number.isInteger(projectId) || projectId <= 0) {
      this.isLoading.set(false);
      return;
    }
    this.projectId.set(projectId);

    try {
      const project = await firstValueFrom(this.projectsApi.getProjectById(projectId));
      this.request.set(this.mapToViewRequestData(project));
    } catch (error) {
      console.error(error);
      // leave request as null
    } finally {
      this.isLoading.set(false);
    }
  }

  private mapToViewRequestData(p: ProjectDto): ViewRequestData {
    const hasSequenceTitle = !!p.estimateYear && p.sequenceNumber != null && p.sequenceNumber !== 'null';
    const estimateNumber = p.estimateNumber?.trim()
      || (hasSequenceTitle ? `${p.estimateYear}-${String(p.sequenceNumber)}` : '');
    const statusMap: Record<number, CeRequestStatus> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Assigned CE',
      3: 'Under Development',
      4: 'Issued',
      5: 'Pending Additional Information',
      6: 'Canceled',
    };
    const status: CeRequestStatus = statusMap[p.projectStatusType] ?? 'Draft';
    const isNewProject = p.isNewProject ?? (this.state.flowProgress().detailsRoute === '/new-project');
    return {
      estimateNumber,
      requestDate: p.submittedDate ?? '',
      status,
      projectStatusType: p.projectStatusType,
      projectTeam: p.projectTeam ?? '',
      afeNumber: p.afeNumber ?? '',
      isNewProject,
      projectTitle: p.projectTitle ?? '',
      estimateTitle: p.estimateTitle ?? '',
      sequenceTitle: hasSequenceTitle ? `${p.estimateYear}-${p.sequenceNumber}` : '',
      projectNumber: p.projectNumber ?? '',
      projectCode: p.projectCode ?? '',
      estimateRequestType: p.estimateRequestType ?? '',
      projectManagementType: p.projectManagementType ?? '',
      cpdepPhase: p.cpdepPhase ?? '',
      designProgress: p.designProgress ?? '',
      anticipatedCompletionDate: p.projectCompletionDate ?? '',
      area: p.area ?? '',
      unit: p.unit ?? '',
      preliminaryCostDeadline: p.deadlineDate ?? '',
      ibp: p.ibpYear ?? '',
      taYear: p.turnaroundYear ?? '',
    };
  }

  protected statusClass(status: CeRequestStatus): string {
    return 'status status--' + status.toLowerCase().replace(/\s+/g, '-');
  }

  protected canEditRequest(): boolean {
    const statusType = this.request()?.projectStatusType;
    return statusType === 0 || statusType === 5;
  }

  protected canGoToResponsePage(): boolean {
    const statusType = this.request()?.projectStatusType;
    const id = this.projectId();
    return !!id && typeof statusType === 'number' && statusType >= 1;
  }

  protected canGoToQuestionnaire(): boolean {
    const statusType = this.request()?.projectStatusType;
    const id = this.projectId();
    return !!id && typeof statusType === 'number' && statusType >= 1;
  }

  protected goBack(): void {
    this.router.navigate(['/']);
  }

  protected editRequest(): void {
    if (!this.canEditRequest()) {
      return;
    }

    const id = this.projectId();
    if (!id) {
      return;
    }

    const targetRoute = this.request()?.isNewProject ? '/new-project' : '/existing-project';
    this.router.navigate([targetRoute], { queryParams: { projectId: id } });
  }

  protected goToResponsePage(): void {
    const id = this.projectId();
    if (!id || !this.canGoToResponsePage()) {
      return;
    }

    this.router.navigate(['/response-page'], { queryParams: { projectId: id } });
  }

  protected goToQuestionnaire(): void {
    const id = this.projectId();
    if (!id || !this.canGoToQuestionnaire()) {
      return;
    }

    this.router.navigate(['/questionnaire'], { queryParams: { projectId: id, readonly: 'true' } });
  }

  private mapFromDraftState(): ViewRequestData {
    const f = this.state.draftForm();
    return {
      estimateNumber: f.sequenceTitle || '-',
      requestDate: new Date().toISOString(),
      status: 'Draft',
      projectTeam: f.projectTeam || '',
      afeNumber: f.afeNumber || '',
      isNewProject: f.isNewProject,
      projectTitle: f.projectTitle || '',
      estimateTitle: f.estimateTitle || '',
      sequenceTitle: f.sequenceTitle || '',
      projectNumber: f.projectNumber || '',
      projectCode: f.projectCode || '',
      estimateRequestType: f.estimateRequestType || '',
      projectManagementType: f.projectManagementType || '',
      cpdepPhase: f.cpdepPhase || '',
      designProgress: f.designProgress || '',
      anticipatedCompletionDate: f.anticipatedCompletionDate || '',
      area: f.area || '',
      unit: f.unit || '',
      preliminaryCostDeadline: f.preliminaryCostDeadline || '',
      ibp: f.ibp || '',
      taYear: f.taYear || '',
    };
  }
}

